import { Chart as ChartJS, registerables, Scale, CoreScaleOptions } from 'chart.js';

import { Chart } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(...registerables);

interface HorizontalBarChartProps {
  labels: Array<string[] | string>;
  dataSets: { data: number[]; backgroundColor: string }[];
  borderRadius?: number;
  customAspectRatio?: number;
  maintainAspectRatio?: boolean;
  customBarPercentage?: number;
  customCategoryPercentage?: number;
  customLayoutPaddingRight?: number;
  dataLabelsFormatter?: (value: string) => void;
  afterFit?: (axis: Scale<CoreScaleOptions>) => void;
  max?: number;
  dataLabelsFontSize?: number;
  ticksFontSize?: number;
}

function HorizontalBarChart({
  labels,
  dataSets,
  borderRadius,
  customAspectRatio,
  maintainAspectRatio = true,
  customBarPercentage,
  customCategoryPercentage,
  customLayoutPaddingRight,
  dataLabelsFormatter,
  afterFit,
  max,
  dataLabelsFontSize,
  ticksFontSize,
}: HorizontalBarChartProps) {
  const _datasets = dataSets.map((dataSet) => {
    return {
      data: dataSet.data,
      backgroundColor: dataSet.backgroundColor,
      borderRadius: borderRadius ? borderRadius : 0,
      borderSkipped: false,
      barPercentage: customBarPercentage || 0.95,
      categoryPercentage: customCategoryPercentage || 1.0,
    };
  });

  const data = {
    labels: labels,
    datasets: [..._datasets],
  };

  return (
    <Chart
      plugins={[ChartDataLabels]}
      type="bar"
      options={{
        events: [],
        responsive: true,
        maintainAspectRatio: maintainAspectRatio,
        aspectRatio: customAspectRatio ? customAspectRatio : 2 / 0.9,
        layout: {
          padding: {
            right: customLayoutPaddingRight ? customLayoutPaddingRight : 15,
          },
        },
        plugins: {
          tooltip: {
            enabled: false,
          },
          legend: {
            display: false,
          },
          datalabels: {
            display: true,
            formatter: dataLabelsFormatter,
            align: 5,
            anchor: 'end',
            color: '#000000',
            font: {
              family: 'Roobert',
              size: dataLabelsFontSize ? dataLabelsFontSize : 16,
            },
          },
        },
        indexAxis: 'y',
        scales: {
          x: {
            grid: {
              color: '#000000',
            },
            ticks: {
              display: false,
              callback: (value, index) => (index === 0 ? ' ' : null),
            },
            border: {
              display: false,
            },
            beginAtZero: true,
          },
          y: {
            max: max,
            grid: {
              display: false,
            },
            border: {
              display: false,
            },
            ticks: {
              color: '#000000',
              font: {
                family: 'Roobert',
                size: ticksFontSize ? ticksFontSize : 16,
                lineHeight: 1.2,
              },
              autoSkip: false,
              padding: 4,
              callback: function (value) {
                const label = typeof value === 'number' ? this.getLabelForValue(value) : value;
                if (typeof label === 'string' && label.length > 16) {
                  const words = label.split(' ');
                  const lines: string[] = [];
                  let currentLine = '';

                  words.forEach((word) => {
                    if (currentLine.length === 0) {
                      currentLine = word;
                    } else if ((currentLine + ' ' + word).length <= 16) {
                      currentLine += ' ' + word;
                    } else {
                      lines.push(currentLine);
                      currentLine = word;
                    }
                  });

                  if (currentLine.length > 0) {
                    lines.push(currentLine);
                  }

                  return lines;
                }
                return label;
              },
            },
            afterFit,
          },
        },
      }}
      data={data}
    />
  );
}

export default HorizontalBarChart;
